import type { WidgetEvent, WidgetEventMap } from "../types.ts";

type Handler = (payload: never) => void;

/** Emitter nhỏ, có chủ đích: handler của khách ném lỗi thì nuốt lại, đừng để
 *  một listener hỏng làm chết luồng chat. */
export class Emitter {
  private map = new Map<WidgetEvent, Set<Handler>>();
  private onError: ((error: unknown) => void) | undefined;

  constructor(onError?: (error: unknown) => void) {
    this.onError = onError;
  }

  on<E extends WidgetEvent>(event: E, handler: (payload: WidgetEventMap[E]) => void): () => void {
    let set = this.map.get(event);
    if (!set) {
      set = new Set();
      this.map.set(event, set);
    }
    set.add(handler as Handler);
    return () => this.off(event, handler);
  }

  off<E extends WidgetEvent>(event: E, handler: (payload: WidgetEventMap[E]) => void): void {
    this.map.get(event)?.delete(handler as Handler);
  }

  emit<E extends WidgetEvent>(event: E, payload: WidgetEventMap[E]): void {
    const set = this.map.get(event);
    if (!set) return;
    for (const handler of Array.from(set)) {
      try {
        (handler as (p: WidgetEventMap[E]) => void)(payload);
      } catch (error) {
        this.onError?.(error);
      }
    }
  }

  /** Có ai đang nghe sự kiện này không — dùng để cảnh báo khi chip bước tiếp
   *  theo không có người xử lý. */
  has(event: WidgetEvent): boolean {
    const set = this.map.get(event);
    return !!set && set.size > 0;
  }

  clear(): void {
    this.map.clear();
  }
}
